import React, { useState, useEffect, useCallback } from 'react';
import { Box, Flex, Heading, Button, Text, Icon, useDisclosure } from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import ReactPaginate from 'react-paginate';
import { useSWRConfig } from 'swr';
import { RefundTabs, RefundFilters, RefundTable, RegisterRefundModal } from 'components/refunds';
import { getRefunds, getRefundCounts, getProgramTypes } from 'api/administrador/refunds';

const PAGE_SIZE = 20;

export default function RefundsAdmin() {
  const { mutate } = useSWRConfig();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [activeTab, setActiveTab] = useState('pending');
  const [counts, setCounts] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [programTypes, setProgramTypes] = useState([]);

  const [filters, setFilters] = useState({
    studentCode: '',
    programTypeId: '',
    reason: '',
  });

  const loadCounts = useCallback(async () => {
    try {
      const data = await getRefundCounts();
      setCounts(data);
    } catch (error) {
      console.error('Error cargando counts:', error);
    }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getRefunds({
        page: currentPage,
        pageSize: PAGE_SIZE,
        status: activeTab,
        filters: filters,
      });
      setItems(response.data || []);
      setTotalPages(response.meta?.pagination?.pageCount || 0);
      setTotalResults(response.meta?.pagination?.total || 0);
    } catch (error) {
      console.error('Error cargando devoluciones:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, currentPage]);

  useEffect(() => {
    getProgramTypes().then(setProgramTypes).catch(console.error);
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleFiltersChange = useCallback((partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setCurrentPage(1);
  }, []);

  const handleRegistered = () => {
    loadCounts();
    loadItems();
    mutate('statsAdmin');
    onClose();
  };

  const start = totalResults === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = Math.min(currentPage * PAGE_SIZE, totalResults);

  return (
    <Box>
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden" border="1px solid #9e46d8">
        <Flex
          bg="linear-gradient(135deg, #574feb 0%, #7c3aed 50%, #8b5cf6 100%)"
          p={5}
          align="center"
          justify="space-between"
        >
          <Flex align="center" gap={3}>
            <Icon as={FiRefreshCw} boxSize={6} color="white" />
            <Heading size="md" color="white">
              Devoluciones
            </Heading>
          </Flex>
          <Button
            bg="white"
            color="#574feb"
            size="sm"
            h="44px"
            minH="44px"
            px={4}
            fontWeight="semibold"
            _hover={{ bg: 'gray.100' }}
            onClick={onOpen}
          >
            + Registrar devolución
          </Button>
        </Flex>

        <RefundTabs activeTab={activeTab} counts={counts} onChange={handleTabChange} />

        <RefundFilters filters={filters} onChange={handleFiltersChange} programTypes={programTypes} />

        <RefundTable items={items} loading={loading} />

        <Flex justify="space-between" align="center" p={4} borderTop="1px solid" borderColor="gray.100">
          <Text color="gray.600" fontSize="sm">
            Mostrando {start}–{end} de {totalResults} resultados
          </Text>
          {totalPages > 1 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel="Siguiente >"
              previousLabel="< Anterior"
              onPageChange={(e) => setCurrentPage(e.selected + 1)}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageCount={totalPages}
              forcePage={currentPage - 1}
              containerClassName="devolucionesPagination"
              pageClassName="devolucionesPaginationItem"
              activeClassName="devolucionesPaginationActive"
              previousClassName="devolucionesPaginationItem"
              nextClassName="devolucionesPaginationItem"
            />
          )}
        </Flex>
      </Box>

      <RegisterRefundModal isOpen={isOpen} onClose={onClose} onSuccess={handleRegistered} />

      <style jsx global>{`
        .devolucionesPagination {
          display: flex;
          gap: 6px;
          list-style: none;
          margin: 0;
          padding: 0;
          align-items: center;
        }
        .devolucionesPaginationItem a {
          padding: 6px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          color: #4a5568;
        }
        .devolucionesPaginationActive a {
          background: #574feb;
          color: white;
          border-color: #574feb;
        }
      `}</style>
    </Box>
  );
}
